// Filtering Utilities
// Functions for filtering SOW data to remove zero/empty values.
// Used by API for saving and by serialization for export.

import type { SOWData, CategoryData, FieldValues } from './sow.types';

// Filter out zero values from FieldValues
function filterNonZeroFields(fields: FieldValues): FieldValues {
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value > 0) {
      result[key] = value;
    }
  }
  return result;
}

// Filter CategoryData to only include non-zero values
// Returns null if the category has no meaningful data
function filterCategoryData(data: CategoryData): CategoryData | null {
  const buildingWide = filterNonZeroFields(data.buildingWide);
  const units = data.units.map(filterNonZeroFields);
  const description = data.description?.trim() || '';

  // Check if there's any data worth keeping
  const hasBuildingWideData = Object.keys(buildingWide).length > 0;
  const hasUnitData = units.some(u => Object.keys(u).length > 0);
  const hasDescription = description.length > 0;

  if (!hasBuildingWideData && !hasUnitData && !hasDescription) {
    return null;
  }

  return {
    buildingWide,
    units: hasUnitData ? units : [],
    description,
  };
}

// Filter SOWData to only include non-zero values
// Returns a partial SOWData suitable for saving/export
export function filterSOWData(data: SOWData): Partial<SOWData> {
  const filteredCategories: Record<string, CategoryData> = {};

  for (const [categoryId, categoryData] of Object.entries(data.categories)) {
    const filtered = filterCategoryData(categoryData);
    if (filtered) {
      filteredCategories[categoryId] = filtered;
    }
  }

  return {
    propertyAddress: data.propertyAddress || undefined,
    propertyType: data.propertyType,
    unitCount: data.unitCount,
    categories: filteredCategories,
    contingency: data.contingency.value > 0 ? data.contingency : undefined,
    subtotal: data.subtotal > 0 ? data.subtotal : undefined,
    total: data.total > 0 ? data.total : undefined,
  };
}
